import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'redux/hooks'
import { Button } from '@frontend/design_system'
import { fleetApi } from 'api'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode'
import {
  isMigratedUserSelector,
  isOperatorSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { PriceListDetailsForm } from '../PriceListDetailsForm'
import { generateSimpleUniqueId } from 'utils/generateUniqueId'
import { FormValues } from '../PriceListDetailsForm/types'
import { DailyRange, FullPriceList, PriceListType } from 'api/fleet/types'
import {
  PRICE_LIST_DETAILS,
  PRICE_LIST_DETAILS_BREADCRUMB,
  PRICE_LIST_TITLE,
} from 'texts/priceListDetails'
import { replaceLastPath } from 'utils/replaceLastPath'
import arrowRight from 'assets/img/arrowRight.webp'
import { formatSeasonRange } from '../../utils/formatSeasonRange'
import { useApiRequest } from 'hooks/useApiRequest'
import { getDetailsBreadcrumbsList } from 'utils/getDetailsBreadcrumbsList'
import { useDispatch } from 'react-redux'
import { setNotificationMessage } from 'redux/notifications/slice'
import { prepareResponseItems, prepareCorForSelect } from 'utils/form'
import { useGetLocations } from '../../hooks/useGetLocations'
import { useCompanyId } from 'hooks/useCompanyId'
import { Container } from 'ui/molecules/Container'
import { URLS } from 'constants/urls'
import { PRICE_LIST_DETAILS_FORM_ID } from '../../constants'
import { FormActionButtons } from 'ui/components/FormActionButtons'
import styles from './styles.module.scss'
import { ACCOUNT_STATUSES, READONLY_STATUSES } from 'api/auth/constants'
import { useMigrationRedirect } from 'hooks/useMigrationRedirect'

export const PriceListDetails = () => {
  const [priceListInfo, setPriceListInfo] = useState<Partial<FullPriceList>>({})
  const { priceListId } = useParams()
  const location = useLocation()
  const isOperator = useAppSelector(isOperatorSelector)
  const isMigratedUser = useAppSelector(isMigratedUserSelector)
  const isMigratedUserFlow = isMigratedUser && !isOperator
  const { redirect } = useMigrationRedirect()
  const isReadOnlyMode = useReadOnlyMode(priceListInfo.year)
  const { sectionsStatuses, accountStatus } = useAppSelector(userDataSelector)
  const isAccountApproved =
    isOperator || accountStatus === ACCOUNT_STATUSES.APPROVED
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const companyId = useCompanyId()
  const { locations, getCurrentLocations, locationsLoading } = useGetLocations(
    String(companyId)
  )

  const priceListDataRequest = useApiRequest((priceListId) =>
    fleetApi.getPriceListDetails(priceListId)
  )
  const updatePriceListRequest = useApiRequest(
    (values) =>
      fleetApi.updatePriceList(
        priceListId as string,
        String(companyId),
        values
      ),
    undefined,
    true,
    undefined,
    false
  )

  const breadcrumbList = getDetailsBreadcrumbsList(
    isOperator,
    String(companyId),
    'priceLists'
  )

  const navigateToRates = () => {
    navigate(replaceLastPath(location.pathname, '/rates'))
  }

  useEffect(() => {
    if (
      !isAccountApproved &&
      READONLY_STATUSES.includes(sectionsStatuses?.priceListsStatus || '')
    ) {
      navigate(`${URLS.FLEET}${URLS.PRICE_LIST}`)
    }
  })

  useEffect(() => {
    const getPriceListDetails = async () => {
      const response = await priceListDataRequest.apiRequest(priceListId)
      if (response) {
        setPriceListInfo(response.data.priceListInfo)
      }
    }
    getPriceListDetails()
  }, [])

  const handleSubmit = async (data: Partial<FormValues>) => {
    const values = {
      ...data,
      priceListType: data.priceListType as PriceListType,
      isActive: priceListInfo.isActive,
      hirePoints:
        data.hirePoints?.map((hirePoint) => Number(hirePoint.value)) || [],
      seasonRanges: data.seasonRanges
        ? formatSeasonRange(data.seasonRanges)
        : [],
      countriesOfResidence: prepareResponseItems(
        data.countriesOfResidence || []
      ),
    }

    const response = await updatePriceListRequest.apiRequest(values)
    if (response) {
      setPriceListInfo(response.data.priceListInfo)
      dispatch(
        setNotificationMessage({
          notificationMessage: PRICE_LIST_DETAILS.SUCCESS_UPDATE,
        })
      )
      if (isMigratedUserFlow) {
        redirect(companyId, 'priceListsStatus')
      }
    }
  }

  const prepareDailyRanges = (dailyRanges: DailyRange[]) => {
    const copiedDailyRanges = structuredClone(dailyRanges)
    const lastIndex = copiedDailyRanges.length - 1
    if (!copiedDailyRanges[lastIndex].rangeEnd) {
      copiedDailyRanges[lastIndex].rangeEnd = '+'
    }
    return copiedDailyRanges
  }

  const currentLocations = useMemo(
    () => getCurrentLocations(priceListInfo.hirePoints || []),
    [priceListInfo, getCurrentLocations]
  )

  const initValues = useMemo(
    () => ({
      hirePoints: currentLocations,
      year: priceListInfo.year ? String(priceListInfo.year) : '',
      dailyRanges: priceListInfo.dailyRanges
        ? prepareDailyRanges(priceListInfo.dailyRanges).map((item) => ({
            ...item,
            uniqueId: generateSimpleUniqueId(),
          }))
        : [],
      seasonRanges:
        priceListInfo?.seasonRanges?.map((item) => ({
          ...item,
          uniqueId: generateSimpleUniqueId(),
        })) || [],
      priceListType: priceListInfo.priceListType,
      comment: priceListInfo.comment || '',
      countriesOfResidence: prepareCorForSelect(
        priceListInfo.countriesOfResidence
      ),
    }),
    [priceListInfo, currentLocations]
  )

  const isLoading = useMemo(
    () => priceListDataRequest.loading || locationsLoading,
    [priceListDataRequest.loading, locationsLoading]
  )

  const handleCancel = () =>
    navigate(`${URLS.FLEET}${URLS.PRICE_LIST}/${companyId}`)

  const renderButtons = () => (
    <div className={styles.buttons}>
      <FormActionButtons
        handleBack={handleCancel}
        loading={updatePriceListRequest.loading}
        formId={PRICE_LIST_DETAILS_FORM_ID}
      />
      {navigateToRates && (
        <div className={styles['button-wrapper']}>
          <Button
            variant="link"
            onClick={navigateToRates}
            label={PRICE_LIST_DETAILS.GO_TO_RATES}
            typographyName="Button1"
            iconRight={<img src={arrowRight} width={16} height={16} />}
          />
        </div>
      )}
    </div>
  )

  return (
    <Container
      title={PRICE_LIST_TITLE}
      breadcrumbList={breadcrumbList}
      currentPageLabel={PRICE_LIST_DETAILS_BREADCRUMB}
      withNavigation={isOperator}
      loading={isLoading}
      rightBlock={renderButtons()}
    >
      <PriceListDetailsForm
        readOnly={isReadOnlyMode}
        handleSubmit={handleSubmit}
        initialValues={initValues}
        locations={locations}
        isEditForm
        formId={PRICE_LIST_DETAILS_FORM_ID}
      />
    </Container>
  )
}
